import type { ServerEvent, ClientEvent } from '@converoom/contracts';
import { REALTIME_POLICY } from '@converoom/contracts';

export type ConnState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/**
 * One WebSocket per tab. Reconnects with jittered backoff and re-subscribes; callers re-fetch missed
 * persistent messages by seq on 'open'. Team Chat messages are delivered to listeners only — never stored here.
 */
export class Realtime {
  private ws: WebSocket | null = null;
  private listeners = new Set<(e: ServerEvent) => void>();
  private stateListeners = new Set<(s: ConnState) => void>();
  private rooms = new Set<string>();
  private projects = new Set<string>();
  private attempt = 0;
  private hb: number | null = null;
  private stopped = false;
  state: ConnState = 'connecting';
  /** Last presence snapshot per project (connection state only — never message content). */
  presence = new Map<string, Extract<ServerEvent, { t: 'presence' }>['members']>();

  start() {
    this.stopped = false;
    this.connect();
  }

  stop() {
    this.stopped = true;
    if (this.hb) clearInterval(this.hb);
    this.ws?.close();
    this.setState('closed');
  }

  private setState(s: ConnState) {
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  private connect() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/v1/realtime`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setState('open');
      for (const r of this.rooms) this.raw({ t: 'room.subscribe', roomId: r });
      for (const p of this.projects) this.raw({ t: 'presence.join', projectId: p });
      if (this.hb) clearInterval(this.hb);
      this.hb = window.setInterval(() => this.raw({ t: 'ping' }), REALTIME_POLICY.heartbeatMs);
    };
    ws.onmessage = (m) => {
      let ev: ServerEvent;
      try {
        ev = JSON.parse(String(m.data));
      } catch {
        return;
      }
      if (ev.t === 'presence') this.presence.set(ev.projectId, ev.members);
      for (const l of this.listeners) l(ev);
    };
    ws.onclose = (e) => {
      if (this.hb) clearInterval(this.hb);
      if (this.stopped) return;
      if (e.code === 4001 || e.code === 4401) {
        this.setState('closed');
        for (const l of this.listeners) l({ t: 'error', code: 'session_ended' });
        return;
      }
      this.setState('reconnecting');
      const delay = Math.min(15_000, 500 * 2 ** this.attempt++) * (0.7 + Math.random() * 0.6);
      setTimeout(() => !this.stopped && this.connect(), delay);
    };
  }

  private raw(ev: ClientEvent) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(ev));
  }

  /** Returns false when offline: callers must not queue (Team Chat has no offline delivery, ADR-004). */
  send(ev: ClientEvent): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(ev));
    return true;
  }

  subscribeRoom(roomId: string) {
    this.rooms.add(roomId);
    this.raw({ t: 'room.subscribe', roomId });
  }
  unsubscribeRoom(roomId: string) {
    this.rooms.delete(roomId);
    this.raw({ t: 'room.unsubscribe', roomId });
  }
  joinProject(projectId: string) {
    this.projects.add(projectId);
    this.raw({ t: 'presence.join', projectId });
  }
  leaveProject(projectId: string) {
    this.projects.delete(projectId);
    this.presence.delete(projectId);
    this.raw({ t: 'presence.leave', projectId });
  }
  on(l: (e: ServerEvent) => void) {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  onState(l: (s: ConnState) => void) {
    this.stateListeners.add(l);
    return () => this.stateListeners.delete(l);
  }
}
